import {
  Box,
  Popper,
  TextField,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { format } from "date-fns";

import { FC, useEffect, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ptbr } from "../../shared/calendar";

const StyledBox = styled(Box)({
  background: "transparent",
  "& .react-datepicker": {
    zIndex: 1500,
    marginTop: { xs: "14px", md: "24px" },
    padding: "15px",
    boxShadow:
      "0px 6px 10px rgba(0, 0, 0, 0.12), 0px 1px 18px rgba(0, 0, 0, 0.06)",
    "& .react-datepicker__month-container": {
      "& .react-datepicker__header": {
        background: "#fff",
        borderBottom: "none",
        "& .react-datepicker__current-month": {
          fontSize: "1.6rem",
          color: "#58595B",
        },
      },
    },
    "& .react-datepicker__navigation-icon::before": {
      borderColor: "#58595B",
    },
    "& .react-datepicker__navigation": {
      top: 20,
      borderColor: "none",
    },
  },

  "& .react-datepicker__day": {
    fontSize: "13px",
    fontFamily: "Open Sans",
    fontWeight: "400",
    width: "40px",
    height: "30px",
    margin: "4px 0px",
    borderRadius: "0px",
    paddingTop: "4px",
  },

  "& .react-datepicker__day-names": {
    fontWeight: "700",
    fontSize: "12px",
    marginTop: "20px",
  },

  "& .react-datepicker__day-name": {
    color: "#58595B",
    width: "auto",
    padding: "0 6px",
  },

  "& .react-datepicker__day--in-range": {
    background: "#E8E9EB",

    color: "#58595B",

    "&:first-of-type": {
      borderRadius: "16px 0px 0px 16px",
    },
    "&:last-child": {
      borderRadius: "0px 30px 30px 0px",
    },
  },
  "& .react-datepicker__day--range-start": {
    paddingTop: "4px",
    width: "40px",
    height: "30px",
    fontWeight: "bold",
    background: "#FFFFFF",
    color: "#CC3D3D !important",
    borderRadius: "30px 0px 0px 30px",
    filter: `drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.06)) drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.08))`,
    boxShadow:
      "0px 3px 5px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.06)",
  },
  "& .react-datepicker__day--range-end": {
    paddingTop: "4px",
    width: "40px",
    height: "30px",
    fontWeight: "bold",
    background: "#FFFFFF",
    color: "#CC3D3D !important",

    borderRadius: "0px 30px 30px 0px",
    filter: `drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.06)) drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.08))`,
    boxShadow:
      "0px 3px 5px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.06)",
  },
  "& .react-datepicker__day--keyboard-selected": {
    backgroundColor: "#FFFFFF",
    color: "#58595B",
  },
  "& .react-datepicker__day--in-selecting-range": {
    background: "#8C8D8F!important",
    color: "#FFF!important",
    "&:first-of-type": {
      borderRadius: "16px 0px 0px 16px",
    },
    "&:last-child": {
      borderRadius: "0px 30px 30px 0px",
    },
  },

  // Year picker styles
  "& .react-datepicker__header--has-year-dropdown": {
    backgroundColor: "#fff",
    borderBottom: "none",
  },
  "& .react-datepicker-year-header": {
    fontSize: "1.8rem",
    fontWeight: "700",
    color: "#58595B",
    fontFamily: "Open Sans",
    backgroundColor: "#fff",
    borderBottom: "none",
    paddingBottom: "8px",
  },
  "& .react-datepicker__year": {
    padding: "8px 0",
  },
  "& .react-datepicker__year-wrapper": {
    display: "grid !important",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "6px 8px",
    maxWidth: "240px",
    justifyItems: "center",
  },
  "& .react-datepicker__year-text": {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontFamily: "Open Sans",
    fontWeight: "400",
    color: "#58595B",
    padding: "8px 16px",
    borderRadius: "20px",
    width: "auto",
    margin: "0",
    cursor: "pointer",
    transition: "background-color 0.15s, color 0.15s",
    "&:hover": {
      backgroundColor: "#E8E9EB",
      color: "#58595B",
    },
  },
  "& .react-datepicker__year-text--selected": {
    backgroundColor: "#E20F1A !important",
    color: "#FFFFFF !important",
    fontWeight: "700",
    "&:hover": {
      backgroundColor: "#CC3D3D !important",
      color: "#FFFFFF !important",
    },
  },
  "& .react-datepicker__year-text--keyboard-selected": {
    backgroundColor: "transparent",
    color: "#58595B",
  },
  "& .react-datepicker__year-text--today": {
    fontWeight: "700",
    color: "#E20F1A",
  },
});

registerLocale("pt-BR", ptbr);

interface CalendarRangeProps {
  name: string;
  label: string;
  onChange?: (date: Date | null) => void;
  disabled?: boolean;
  isCleanFilter?: boolean;
  value?: Date;
  errorAlternative?: boolean;
  excludeDateIntervals?: Array<{ start: Date; end: Date }>;
  minDate?: Date;
  maxDate?: Date;
  helperText?: string;
  isOpen?: boolean;
  includeDates?: Date[];
  yearOnly?: boolean;
}

const CalendarSingleInput: FC<CalendarRangeProps> = ({
  name,
  label,
  onChange,
  disabled = false,
  isCleanFilter,
  value,
  errorAlternative,
  excludeDateIntervals,
  minDate,
  maxDate,
  helperText,
  isOpen: isOpenDefault,
  includeDates,
  yearOnly = false,
}) => {
  const [date, setDate] = useState<Date | null>(value ?? null);
  const [isOpen, setIsOpen] = useState(isOpenDefault ?? false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  useEffect(() => {
    setDate(value ?? null);
  }, [value]);

  useEffect(() => {
    setIsOpen(isOpenDefault ?? false);
  }, [isOpenDefault]);

  const handleChange = (date: Date | null) => {
    setDate(date);
    onChange?.(date);
    setIsOpen(false);
  };

  const toggleDatePicker = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isCleanFilter) {
      setDate(null);
    }
  }, [isCleanFilter]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest(".react-datepicker")
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <Box ref={wrapperRef} sx={{ width: "100%" }}>
      {" "}
      <Typography
        fontSize={"14px"}
        sx={{
          color: theme.palette.tagAndStatusText[3],
          fontWeight: "700",
          paddingBottom: "4px",
          display: "block",
        }}
      >
        {label}
      </Typography>
      <TextField
        disabled={disabled}
        name={name}
        error={errorAlternative}
        helperText={helperText}
        placeholder={yearOnly ? "aaaa" : "dd/mm/aaaa"}
        value={date ? format(date, yearOnly ? "yyyy" : "dd/MM/yyyy") : ""}
        onClick={toggleDatePicker}
        autoComplete="off"
        fullWidth
        variant="outlined"
        InputProps={{
          readOnly: true,
        }}
        sx={{
          width: "100%",
          backgroundColor: "#fff",
          maxHeight: "40.3px",
          "& .Mui-disabled": {
            opacity: 0.7,
            background: `${theme.palette.shadesOfDark.ultraLight}`,
            WebkitTextFillColor: `${theme.palette.shadesOfDark.black}`,
          },
          "& .datepicker": {
            border: "2px solid #58595B",
          },
          "& .MuiInputLabel-shrink": {
            fontWeight: 700,
            backgroundColor: "#fff",
            borderRadius: "4px",
            color: theme.palette.tagAndStatusText[3],
            paddingRight: 6,
            fontFamily: "Open Sans",
            fontSize: "20px",
          },
          "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: `2px solid ${theme.palette.primary.main}`,
          },
          "& .MuiOutlinedInput": {
            fontSize: "20px",
          },
          "& .MuiOutlinedInput-input": {
            fontSize: "14px",
            padding: "4px 8px",
            fontFamily: "Open Sans",
            color: "#58595B",
            height: "32px",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: "4px",
          },
        }}
      />
      {isOpen && (
        <Popper
          open={isOpen}
          anchorEl={wrapperRef.current}
          placement="bottom-start"
          style={{ zIndex: 1500 }}
          disablePortal
        >
          <StyledBox onClick={(e) => e.stopPropagation()}>
            <DatePicker
              selected={date}
              onChange={handleChange}
              monthsShown={1}
              locale={ptbr}
              inline
              shouldCloseOnSelect={true}
            />
          </StyledBox>
        </Popper>
      )}
    </Box>
  );
};
export default CalendarSingleInput;
